import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress, Container,
    Grid,
    TextField,
    Typography
} from "@mui/material";
import React, {useDebugValue, useEffect, useState} from "react";
import {EditableCargoList} from "~/apps/packages/auto-visits/components/AutoVisitForm/EditableCargoList";
import {DriverSelect} from "~/apps/packages/auto-visits/components/AutoVisitForm/DriverSelect";
import {VehicleSelect} from "~/apps/packages/auto-visits/components/AutoVisitForm/VehicleSelect";
import {useForm, Controller} from "react-hook-form";
import moment from "moment";
import {TimeSlotSelect} from "~/apps/packages/auto-visits/components/AutoVisitForm/TimeSlotSelect";
import {CheckpointSelect} from "~/apps/packages/auto-visits/components/AutoVisitForm/CheckpointSelect";
import {
    AutoVisit,
    new2oldDriver,
    new2oldVehicle,
    old2newDriver,
    old2newVehicle
} from "~/apps/packages/auto-visits/queries";
import {Done} from "~/ui/icons";
import {FloatingActions} from "~/apps/core/ui/FloatingActions";
import {DateField} from "~/apps/core/ui/DateField";


export type AutoVisitFormProps = {
    onSubmit?: (value: FormValues) => any | Promise<any>
    defaultValue?: FormValues
    carrierId: string
    autoVisitId?: string
    variant: "create" | "edit"
}


export type FormValues = {
    Date: Date
    TimeSlot: AutoVisit["TimeSlot"]
    CheckPoint: AutoVisit["CheckPoint"]
    Vehicle: AutoVisit["Vehicle"]
    Driver: AutoVisit["Driver"]
    CargoList: AutoVisit["CargoList"]
    ServiceList: AutoVisit["ServiceList"]
}


const FormCard = (props) => (
    <Card>
        <CardContent>
            <Grid container direction="column" spacing={2}>
                {React.Children.map(props.children, (item, index) => (
                    <Grid item key={index}>
                        {item}
                    </Grid>
                ))}
            </Grid>
        </CardContent>
    </Card>
)


export const AutoVisitForm = (props: AutoVisitFormProps) => {
    const {onSubmit, defaultValue, carrierId, autoVisitId, variant} = props;
    const {handleSubmit: submitWrapper, getValues, watch, control, errors, formState, setValue} = useForm<FormValues>({
        defaultValues: {
            Driver: null,
            Vehicle: null,
            TimeSlot: null,
            CheckPoint: null,
            Date: moment().toDate(),
            CargoList: [],
            ServiceList: [],
        },
        mode: "all",
    });
    const serviceIds = watch("ServiceList").map(item => item.ServiceID);
    const cargoIds = watch("CargoList").map(item => item.CargoID);
    const timeslotId = watch("TimeSlot", false as any)?.ID;
    const date = watch("Date") ? moment(watch("Date")).toDate() : null;

    const {isValid, isSubmitting, isDirty} = formState;
    const submitDisabled = !isValid || isSubmitting || !isDirty;

    const handleSubmit = submitWrapper(() => {
        return onSubmit && onSubmit(getValues());
    });

    useEffect(() => {
        if (defaultValue) {
            for (const [key, value] of Object.entries(defaultValue)) {
                setValue(key, value, {shouldValidate: true});
            }
        }
    }, [])

    return (
        <Box m={2}>
            <form onSubmit={handleSubmit}>
                <Grid container direction="column" rowSpacing={2}>
                    <Grid item>
                        <FormCard>
                            <Controller
                                name="Vehicle"
                                control={control}
                                rules={{required: true}}
                                render={({onChange, value}) => (
                                    <VehicleSelect
                                        onChange={value => {
                                            onChange(new2oldVehicle(value))
                                            if (value.driver) {
                                                setValue("Driver", new2oldDriver(value.driver), {shouldValidate: true});
                                            }
                                        }}
                                        value={old2newVehicle(value)}
                                        additionalOptions={defaultValue?.Vehicle ? [old2newVehicle(defaultValue.Vehicle)] : []}
                                        fullWidth
                                        disabled={isSubmitting}
                                    />
                                )}
                            />
                            <Controller
                                name="Driver"
                                control={control}
                                rules={{required: true}}
                                render={({onChange, value}) => (
                                    <DriverSelect
                                        onChange={value => onChange(new2oldDriver(value))}
                                        value={old2newDriver(value)}
                                        additionalOptions={defaultValue?.Driver ? [old2newDriver(defaultValue.Driver)] : []}
                                        fullWidth
                                        disabled={isSubmitting}
                                    />
                                )}
                            />
                        </FormCard>
                    </Grid>
                    <Grid item>
                        <Typography variant="screenText">
                            Грузы
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Controller
                            name="CargoList"
                            control={control}
                            rules={{
                                required: true,
                                validate: value => {
                                    if (!value.length) {
                                        return "Не выбран груз";
                                    }
                                }
                            }}
                            render={({onChange: onChangeCargoList, value: CargoList}) => (
                                <Controller
                                    name="ServiceList"
                                    control={control}
                                    rules={{required: true, minLength: 1}}
                                    render={({onChange: onChangeServiceList, value: ServiceList}) => (
                                        <EditableCargoList
                                            carrierId={carrierId}
                                            autoVisitId={autoVisitId}
                                            value={CargoList.map(cargoItem => {
                                                const cargoServiceList = [];
                                                for (const serviceItem of ServiceList) {
                                                    if (serviceItem.CargoID == cargoItem.CargoID) {
                                                        cargoServiceList.push({
                                                            ...serviceItem,
                                                            ID: serviceItem.ServiceID,
                                                        });
                                                    }
                                                }
                                                return {
                                                    ...cargoItem,
                                                    ID: cargoItem.CargoID,
                                                    ServiceList: cargoServiceList,
                                                };
                                            })}
                                            onChange={value => {
                                                const ServiceList = [];
                                                const CargoList = [];
                                                for (const cargo of value) {
                                                    CargoList.push({
                                                        ...cargo,
                                                        CargoID: cargo.ID,
                                                        ID: "",
                                                    });
                                                    for (const service of cargo.ServiceList) {
                                                        ServiceList.push({
                                                            ...service,
                                                            ServiceID: service.ID,
                                                            ID: "",
                                                            CargoID: cargo.ID
                                                        });
                                                    }
                                                }

                                                onChangeServiceList(ServiceList);
                                                onChangeCargoList(CargoList);
                                            }}
                                            disabled={isSubmitting}
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item>
                        <FormCard>
                            <Controller
                                name="Date"
                                control={control}
                                rules={{
                                    required: true,
                                    validate: value => {
                                        // for edit exist auto visit
                                        if (defaultValue?.Date && moment(value).diff(defaultValue.Date, "day") == 0) {
                                            return;
                                        }
                                        if (moment(value).diff(moment(), "day") < 0) {
                                            return "Выбрана прошедшая дата";
                                        }
                                    }
                                }}
                                render={({onChange, value}) => (
                                    <DateField
                                        label="Дата визита"
                                        fullWidth
                                        error={Boolean(errors?.Date?.message)}
                                        helperText={errors?.Date?.message}
                                        value={value ? moment(value) : value}
                                        onChange={value => {
                                            onChange(value);
                                        }}
                                        disabled={isSubmitting || !cargoIds.length}
                                    />
                                )}
                            />

                            <Controller
                                name="TimeSlot"
                                control={control}
                                rules={{required: true}}
                                render={({onChange, value}) => (
                                    <TimeSlotSelect
                                        fullWidth
                                        autoVisitId={autoVisitId}
                                        autoVisitDate={errors.Date || formState.isValidating ? null : date}
                                        carrierId={carrierId}
                                        serviceIds={serviceIds}
                                        cargoIds={cargoIds}
                                        value={formState.isValidating ? null : value}
                                        onChange={value => onChange(value)}
                                        disabled={isSubmitting || !cargoIds.length}
                                    />
                                )}
                            />

                            <Controller
                                name="CheckPoint"
                                control={control}
                                rules={{required: true}}
                                render={({onChange, value}) => (
                                    <CheckpointSelect
                                        carrierId={carrierId}
                                        cargoIds={cargoIds}
                                        autoVisitId={autoVisitId}
                                        autoVisitDate={date}
                                        timeslotId={timeslotId}
                                        onChange={value => onChange(value)}
                                        value={value}
                                        fullWidth
                                        disabled={isSubmitting || !cargoIds.length || !timeslotId}
                                    />
                                )}
                            />
                        </FormCard>
                    </Grid>
                </Grid>
                <Box height="80px"/>
                <FloatingActions>
                    <Button
                        type="submit"
                        fullWidth
                        color="primary"
                        disabled={submitDisabled}
                        startIcon={isSubmitting ? (<CircularProgress size={16}/>) : (<Done/>)}
                    >
                        {variant == "create" && (
                            <>
                                Создать
                            </>
                        )}
                        {variant == "edit" && (
                            <>
                                Сохранить
                            </>
                        )}
                    </Button>
                </FloatingActions>
            </form>
        </Box>
    );
}
